import React, { FunctionComponent, memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'react-final-form';
import { FilterLocations } from '../../common/FilterLocations';
import {
  filterButtonStyles,
  FilterWrapper,
  SearchBarWrapper,
  SearchPageFiltersContainer,
  GoBackButton
} from './styled';
import { BuildingTypeFilter } from '../BuildingTypeFilter';
import useScreenSize from '../../../../hooks/useScreenSize';
import { LeasedFilter } from '../LeasedFilter';
import { Button } from '../../../ui/Button';
import { ButtonType } from '../../../../styled/theme/types';
import Icon from '../../../Icon';
import FilterIcon from '../../../../public/static/images/new_search/icons/icon-filter.svg';
import { RoomsFilter } from '../RoomsFilter';
import { FLAT_CATEGORIES } from '../../../../utils/helpers/constants';
import { AreaFilter } from '../AreaFilter';
import { PriceFilter } from '../PriceFilter';
import { FilterFieldProps, ModalParams, ModalTypeEnum } from '../types';
import { useClient } from '../../../../hooks/useClient';
import { Category } from '../../../../graphql/types';

type SearchPageFiltersProps = {
  shortMode?: boolean;
  canGoBack?: boolean;
  isShowHomeFilters?: boolean;
  onGoBack?: () => void;
  setModalParams?: React.Dispatch<React.SetStateAction<ModalParams>>;
  isPashaLanding?: boolean;
  additionalButtons?: unknown;
  categories?: Category[];
};

type FiltersConfig = {
  component: FunctionComponent<FilterFieldProps>;
  order: number;
  condition?: boolean;
};

export const SearchPageFilters = memo(
  ({
    setModalParams,
    shortMode,
    canGoBack,
    isShowHomeFilters,
    onGoBack,
    isPashaLanding,
    categories,
    additionalButtons = null
  }: SearchPageFiltersProps) => {
    const { t } = useTranslation();
    const isClient = useClient();
    const { isSmall, isLarge, isMedium } = useScreenSize();
    const { input: categoryField } = useField('category_id', {
      subscription: { value: true }
    });

    const isFlatCategory = useMemo(
      () => FLAT_CATEGORIES.includes(categoryField.value),
      [categoryField.value]
    );

    const isSmallSafe = isClient && isSmall;
    const isMediumSafe = isClient && isMedium;
    const isLargeSafe = isClient && isLarge;

    const openFilters = useCallback(() => {
      if (setModalParams) {
        setModalParams({ initialScreen: ModalTypeEnum.Filters });
      }
    }, [setModalParams]);

    const filtersConfig: FiltersConfig[] = useMemo(
      () => [
        { component: LeasedFilter, order: 1 },
        { component: BuildingTypeFilter, order: 2 },
        { component: PriceFilter, order: isSmallSafe ? 3 : 4 },
        {
          component: isFlatCategory ? RoomsFilter : AreaFilter,
          condition: isShowHomeFilters || isPashaLanding ? true : !isMediumSafe,
          order: isSmallSafe ? 4 : 3
        }
      ],
      [
        isSmallSafe,
        isFlatCategory,
        isShowHomeFilters,
        isPashaLanding,
        isMediumSafe
      ]
    );

    const sortedFiltersConfig = useMemo(
      () =>
        filtersConfig
          .filter(({ condition = true }) => condition)
          .sort((a, b) => a.order - b.order),
      [filtersConfig]
    );

    const memoFilterBtnStyles = useMemo(
      () => filterButtonStyles(isShowHomeFilters ?? false),
      [isShowHomeFilters]
    );

    return (
      <SearchPageFiltersContainer
        $canGoBack={canGoBack}
        $isShowHomeFilters={isShowHomeFilters}
      >
        {canGoBack && (
          <GoBackButton data-cy="go-back-button" onClick={onGoBack} />
        )}

        {!shortMode && (
          <>
            {sortedFiltersConfig.map(
              ({ component: FilterComponent, order }) => (
                <FilterWrapper
                  key={order.toString()}
                  $isPashaLanding={isPashaLanding}
                >
                  <FilterComponent
                    submitOnChange
                    isPashaLanding={isPashaLanding}
                    categories={categories}
                  />
                </FilterWrapper>
              )
            )}
          </>
        )}

        {!isPashaLanding && (
          <>
            <SearchBarWrapper
              canGoBack={canGoBack && shortMode}
              $isShowHomeFilters={isShowHomeFilters}
            >
              <FilterLocations setModalParams={setModalParams} />
            </SearchBarWrapper>
            <Button
              onClick={openFilters}
              onKeyPress={openFilters}
              cypress="new-search-filters-button"
              stat="new-search-filters-button"
              styles={memoFilterBtnStyles}
              ariaLabel={t('search.buttons.filters')}
              title={t('search.buttons.filters')}
              state={ButtonType.white}
            >
              <Icon
                width={20}
                height={20}
                indent={false}
                IconComponent={FilterIcon}
              />
              {isLargeSafe && t('search.buttons.filters')}
            </Button>
          </>
        )}
        {additionalButtons}
      </SearchPageFiltersContainer>
    );
  }
);
SearchPageFilters.displayName = 'SearchPageFilters';
